import type { ComponentType, ComponentProps, SVGProps } from "react";
import { IconApplicationStatusApprovalDocument, IconFileFill, IconFileListReport, IconProjectListReport } from "@synthesis/shared-icons";
import { makeAutoObservable } from "mobx";
import { type Tag } from "@synthesis/shared-ui";
import { makeStoreProvider } from "@synthesis/shared-utils";

// ---------------------------------- TYPES ----------------------------------

export type ConstructorServiceStatus = "ACTIVE" | "UNAVAILABLE";

export type ConstructorService = {
  id: string;
  title: string;
  description: string;
  code: string;
  /** Сводка по версиям процесса услуги (отображается двумя цветами). */
  versions: {
    activeLabel: string;
    archivedLabel: string;
  };
  documentKitsCount: number;
  status: ConstructorServiceStatus;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
};

// ---------------------------------- MOCK DATA ----------------------------------
// Бэкенда конструктора услуг ещё нет — данные захардкожены по макетам
// «Конструктор - Услуги» / «Справочники» (Figma node 5792-49586). Заменить на API-хуки после появления сервиса.

export const MOCK_CONSTRUCTOR_SERVICES: ConstructorService[] = [
  {
    id: "tu-issue",
    title: "Выдача ТУ",
    description: "Выдача технических условий на примыкание, пересечение или врезку в инженерные сети.",
    code: "TU-ISSUE",
    versions: { activeLabel: "3 действующие", archivedLabel: "1 в архиве" },
    documentKitsCount: 2,
    status: "ACTIVE",
    icon: IconApplicationStatusApprovalDocument,
  },
  {
    id: "ppr",
    title: "ППР",
    description: "Согласование проекта производства работ вблизи железнодорожных путей.",
    code: "PPR",
    versions: { activeLabel: "2 действующие", archivedLabel: "1 в архиве" },
    documentKitsCount: 2,
    status: "UNAVAILABLE",
    icon: IconProjectListReport,
  },
  {
    id: "doc-approve",
    title: "Согласование документации",
    // В макете у карточки скопировано описание «Выдачи ТУ» — мок осмысленная формулировка.
    description: "Согласование проектной и технической документации при выполнении работ.",
    code: "DOC-APPROVE",
    versions: { activeLabel: "3 действующие", archivedLabel: "1 в архиве" },
    documentKitsCount: 1,
    status: "UNAVAILABLE",
    icon: IconFileListReport,
  },
  {
    id: "akt-dopuska",
    title: "Акт-допуска",
    description: "Оформление акта-допуска для производства работ в охранной зоне железной дороги.",
    // В макете у «Акта-допуска» стоит код «PPR» (дубль) — мок осмысленный код.
    code: "AKT-DOPUSK",
    versions: { activeLabel: "2 действующие", archivedLabel: "1 в архиве" },
    documentKitsCount: 1,
    status: "UNAVAILABLE",
    icon: IconFileFill,
  },
];

/** Счётчики табов «Дороги» и «Документы» перенесены в @/components/admin-console/admin-console.constants. */

// ---------------------------------- HELPERS ----------------------------------

export const filterConstructorServices = (services: ConstructorService[], search: string): ConstructorService[] => {
  const query = search.trim().toLowerCase();

  if (!query) {
    return services;
  }

  return services.filter((service) => service.title.toLowerCase().includes(query) || service.code.toLowerCase().includes(query));
};

export const getDocumentKitsLabel = (count: number): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return `${count} комплект`;
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `${count} комплекта`;
  }

  return `${count} комплектов`;
};

export const CONSTRUCTOR_SERVICE_STATUS_TAG: Record<
  ConstructorServiceStatus,
  { tagStatus: NonNullable<ComponentProps<typeof Tag>["status"]>; label: string; withLeftIcon: boolean }
> = {
  ACTIVE: { tagStatus: "SUCCESS", label: "Активна", withLeftIcon: true },
  UNAVAILABLE: { tagStatus: "NEUTRAL", label: "Недоступно", withLeftIcon: false },
};

// ---------------------------------- STORE ----------------------------------

export type ServiceModalAction = { type: "add-service" };

class PageStore {
  public popupAction: ServiceModalAction | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public setPopupAction(action: ServiceModalAction | null) {
    this.popupAction = action;
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
